import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, SignOut, Storefront, Plus, PencilSimple } from "@phosphor-icons/react";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";

const STATUS_LABELS = {
  active: "Assinatura ativa",
  trialing: "Em teste (Stripe)",
  past_due: "Pagamento pendente",
  canceled: "Cancelada",
  none: "Sem assinatura",
};
const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-400",
  trialing: "bg-amber-500/15 text-amber-500",
  past_due: "bg-orange-500/15 text-orange-400",
  canceled: "bg-red-500/15 text-red-400",
  none: "bg-zinc-700/40 text-zinc-400",
};

export default function PartnerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("todas");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ shop_name: "", whatsapp: "", password: "" });
  const [creating, setCreating] = useState(false);

  const createBarber = async (e) => {
    e.preventDefault();
    if (createForm.shop_name.trim().length < 2 || createForm.whatsapp.replace(/\D/g, "").length < 8 || createForm.password.length < 6) {
      return toast.warning("Preencha nome, WhatsApp e senha temporária (mín. 6)");
    }
    setCreating(true);
    try {
      await axios.post(`${API}/partner/barbers`, createForm, { headers: authHeaders() });
      toast.success(`Página criada! Repasse ao barbeiro: WhatsApp + senha temporária. Ele definirá a própria senha no primeiro acesso.`);
      setShowCreate(false);
      setCreateForm({ shop_name: "", whatsapp: "", password: "" });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar barbearia");
    } finally {
      setCreating(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/overview`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        navigate("/entrar");
      } else if (e.response?.status === 403) {
        navigate("/painel");
      } else {
        toast.error("Erro ao carregar dados");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) return navigate("/entrar");
    load();
  }, [load, navigate]);

  const logout = () => {
    clearToken();
    navigate("/entrar");
  };

  const digits = search.replace(/\D/g, "");
  const q = search.toLowerCase().trim();
  const matchesSearch = (b) => {
    if (!q) return true;
    const text = `${b.shop_name} ${b.name} ${b.city} ${b.slug}`.toLowerCase();
    if (text.includes(q)) return true;
    return digits.length >= 3 && (b.whatsapp || "").includes(digits);
  };
  const matchesFilter = (b) => {
    if (filter === "ativas") return b.subscription_active;
    if (filter === "teste") return b.subscription_status === "none" && b.trial_days_left > 0 && b.paid_days_left === 0;
    if (filter === "pausadas") return !b.subscription_active;
    return true;
  };
  const barbers = (data?.barbers || []).filter((b) => matchesSearch(b) && matchesFilter(b));
  const stats = data?.stats;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 py-6 pb-16" data-testid="partner-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-500">
            <Scissors size={18} weight="duotone" />
            <span className="font-display font-bold">Barber Menu · Sócio</span>
          </div>
          <button
            data-testid="partner-logout-btn"
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400 transition-colors"
          >
            <SignOut size={16} weight="bold" /> Sair
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Visão Brasil · crie e personalize as páginas</p>

        <button
          data-testid="partner-create-toggle"
          onClick={() => setShowCreate(!showCreate)}
          className="mt-4 w-full h-11 rounded-lg border border-dashed border-zinc-700 text-zinc-400 text-sm font-semibold flex items-center justify-center gap-2 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
        >
          <Plus size={16} weight="bold" /> Nova barbearia
        </button>

        {showCreate && (
          <form onSubmit={createBarber} className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="partner-create-form">
            <input data-testid="partner-create-name" type="text" value={createForm.shop_name} onChange={(e) => setCreateForm((f) => ({ ...f, shop_name: e.target.value }))} placeholder="Nome da barbearia" className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm" />
            <input data-testid="partner-create-whatsapp" type="tel" value={createForm.whatsapp} onChange={(e) => setCreateForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="WhatsApp do barbeiro (login)" className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm" />
            <input data-testid="partner-create-password" type="text" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} placeholder="Senha temporária (mín. 6)" className="w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm" />
            <p className="text-[11px] text-zinc-600">O barbeiro define a própria senha no primeiro acesso.</p>
            <button data-testid="partner-create-submit" type="submit" disabled={creating} className="w-full h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60">
              {creating ? "Criando..." : "Criar página"}
            </button>
          </form>
        )}

        <div className="grid grid-cols-2 gap-3 mt-5" data-testid="partner-stats">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-stat-total">
            <p className="text-2xl font-black text-zinc-50">{stats?.total ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Barbearias BR</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-stat-active">
            <p className="text-2xl font-black text-emerald-400">{stats?.active ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Ativas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-stat-paused">
            <p className="text-2xl font-black text-red-400">{stats?.paused ?? "—"}</p>
            <p className="text-xs text-zinc-500 mt-1">Pausadas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-stat-mrr">
            <p className="text-2xl font-black text-amber-500">
              {stats ? `R$ ${stats.mrr_estimate_brl.toFixed(2).replace(".", ",")}` : "—"}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Receita mensal estimada</p>
          </div>
        </div>

        {stats && (
          <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-xl p-4 grid grid-cols-2 gap-y-2 text-xs text-zinc-400" data-testid="partner-stats-detail">
            <p>Em teste grátis: <span className="font-bold text-zinc-200">{stats.in_trial}</span></p>
            <p>Ativas por código: <span className="font-bold text-zinc-200">{stats.via_code}</span></p>
            <p>Assinatura cartão: <span className="font-bold text-zinc-200">{stats.stripe_active}</span></p>
            <p>Agendamentos: <span className="font-bold text-zinc-200">{stats.total_bookings}</span></p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          <input
            data-testid="partner-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, cidade ou telefone..."
            className="w-full h-11 px-4 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
          />
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="partner-filters">
            {[["todas", "Todas"], ["ativas", "Ativas"], ["teste", "Em teste"], ["pausadas", "Pausadas"]].map(([key, label]) => (
              <button
                key={key}
                data-testid={`partner-filter-${key}`}
                onClick={() => setFilter(key)}
                className={`shrink-0 px-3.5 h-8 rounded-lg text-xs font-semibold transition-colors ${
                  filter === key ? "bg-amber-500 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {!data ? (
            <div className="flex justify-center py-10">
              <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            </div>
          ) : barbers.length === 0 ? (
            <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="partner-no-results">
              Nenhuma barbearia encontrada.
            </p>
          ) : (
            <div className="space-y-3" data-testid="partner-barbers-list">
              {barbers.map((b) => (
                <div key={b.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`partner-barber-row-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Storefront size={18} weight="duotone" className="text-amber-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-bold text-zinc-100 truncate">{b.shop_name}</p>
                        <p className="text-xs text-zinc-500 truncate">{b.whatsapp || "—"}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ${STATUS_STYLES[b.subscription_status] || STATUS_STYLES.none}`} data-testid={`partner-barber-status-${b.id}`}>
                      {STATUS_LABELS[b.subscription_status] || b.subscription_status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-zinc-400">
                    <span>{b.city || "—"}</span>
                    {b.subscription_status === "none" && b.trial_days_left > 0 && b.paid_days_left === 0 && (
                      <span className="text-amber-500 font-semibold">Teste grátis: {b.trial_days_left}d restantes</span>
                    )}
                    {b.subscription_status === "none" && b.paid_days_left > 0 && (
                      <span className="text-emerald-400 font-semibold">Código: {b.paid_days_left}d restantes</span>
                    )}
                    <span>{b.bookings_count} {b.bookings_count === 1 ? "agendamento" : "agendamentos"}</span>
                    <button
                      onClick={() => navigate(`/socio/barbearia/${b.id}`)}
                      className="flex items-center gap-1 text-zinc-300 hover:text-amber-500 font-semibold transition-colors"
                      data-testid={`partner-barber-edit-${b.id}`}
                    >
                      <PencilSimple size={13} weight="bold" /> Editar
                    </button>
                    <a
                      href={`/b/${b.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-500 hover:underline"
                      data-testid={`partner-barber-link-${b.id}`}
                    >
                      /b/{b.slug}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
